(function() {
  'use strict';

  // ============================================================================
  // PRICE MANAGER
  // ============================================================================
  class PriceManager {
    constructor(form) {
      this.form = form;

      this.variantSelect =
        form.querySelector('select[name="id"]') ||
        form.querySelector('input[name="id"]');

      this.totalDisplay = document.getElementById('onlyyou_total_price');

      // IMPORTANT:
      // Your delivery block sets hidden input: data-ai-prop="fee"
      // PriceManager expects #onlyyou_prop_fee (your original code)
      // So we support BOTH.
      this.deliveryFeeInput =
        document.getElementById('onlyyou_prop_fee') ||
        form.querySelector('input[data-ai-prop="fee"]');

      this.giftContainer = document.querySelector('[data-gift-upsell]');

      if (!this.variantSelect || !this.totalDisplay) {
        console.warn('[PriceManager] Required elements not found');
        return;
      }

      this.currentVariantPrice = 0;
      this.currentDeliveryFee = 0;
      this.currentGiftsPrice = 0;

      this.setupListeners();
      this.updatePrice();
    }

    setupListeners() {
      this.variantSelect.addEventListener('change', () => this.updatePrice());
      this.form.addEventListener('change', () => this.updatePrice());

      // Delivery updates
      document.addEventListener('delivery:updated', () => this.updatePrice());

      // Gifts updates
      document.addEventListener('onlyyou:gifts-updated', () => this.updatePrice());
    }

    async updatePrice() {
      this.currentVariantPrice = await this.getVariantPrice();
      this.currentDeliveryFee = this.getDeliveryFee();
      this.currentGiftsPrice = await this.getGiftsPrice();

      const total = this.currentVariantPrice + this.currentDeliveryFee + this.currentGiftsPrice;
      this.displayTotal(total);
    }

    async getVariantPrice() {
      const variantId = this.variantSelect.value;
      if (!variantId) return 0;

      try {
        const res = await fetch(`/variants/${variantId}.js`);
        const variant = await res.json();
        return variant.price || 0;
      } catch (e) {
        console.error('[PriceManager] Error fetching variant', e);
        return 0;
      }
    }

    getDeliveryFee() {
      if (!this.deliveryFeeInput) return 0;

      const feeString = this.deliveryFeeInput.value || '';
      const feeNumber = parseFloat(feeString.replace(/[^0-9.]/g, ''));

      return isFinite(feeNumber) ? Math.round(feeNumber * 100) : 0;
    }

    async getGiftsPrice() {
      if (!this.giftContainer) return 0;

      const selectedGifts = Array.from(
        this.giftContainer.querySelectorAll('[data-gift-variant-id].is-added')
      );

      if (!selectedGifts.length) return 0;

      let total = 0;
      for (const gift of selectedGifts) {
        const variantId = gift.dataset.giftVariantId;
        try {
          const res = await fetch(`/variants/${variantId}.js`);
          const variant = await res.json();
          total += variant.price || 0;
        } catch (e) {
          console.error('[PriceManager] Error fetching gift variant', e);
        }
      }

      return total;
    }

    displayTotal(totalCents) {
      const formatted = this.formatMoney(totalCents);
      this.totalDisplay.textContent = formatted + ' / ';

      const btnText = this.form.querySelector('.add-to-cart-text__content');
      if (btnText) {
        if (!btnText.dataset.original) {
          btnText.dataset.original = btnText.textContent.trim();
        }
        btnText.textContent = `${btnText.dataset.original} • ${formatted}`;
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // ============================================================================
  // CART CLEANER (remove fee if only fee left)
  // ============================================================================
  class CartCleaner {
    constructor() {
      this.deliveryFeeText = 'delivery fee';
      this.init();
    }

    init() {
      document.addEventListener('cart:updated', () => this.cleanupDeliveryFee());
      document.addEventListener('cart:refresh', () => this.cleanupDeliveryFee());

      setTimeout(() => this.cleanupDeliveryFee(), 600);

      // Hide remove button for fee line
      const obs = new MutationObserver(() => {
        this.hideDeliveryFeeControls();
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }

    hideDeliveryFeeControls() {
      document.querySelectorAll('.cart-items__table-row').forEach((row) => {
        const titleEl = row.querySelector('.cart-items__title');
        const txt = (titleEl?.textContent || '').toLowerCase();
        if (!txt.includes(this.deliveryFeeText)) return;

        const qty = row.querySelector('.cart-items__quantity');
        const remove = row.querySelector('.cart-items__remove');
        if (qty) qty.style.display = 'none';
        if (remove) remove.style.display = 'none';
      });
    }

    async cleanupDeliveryFee() {
      try {
        const cart = await this.getCart();
        if (!cart?.items) return;

        const feeItems = cart.items.filter((item) => {
          const title = (item.product_title || item.title || '').toLowerCase();
          return title.includes(this.deliveryFeeText) || item.properties?._delivery_fee === 'true';
        });

        if (!feeItems.length) return;

        const regularItems = cart.items.length - feeItems.length;

        if (regularItems === 0) {
          for (const item of feeItems) {
            await this.removeItem(item.key);
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (e) {
        console.error('[CartCleaner] cleanup error', e);
      }
    }

    async getCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      return await res.json();
    }

    async removeItem(key) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    }
  }

  // ============================================================================
  // INIT
  // ============================================================================
  function init() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    new PriceManager(productForm);
    new CartCleaner();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
